import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Sparkles } from 'lucide-react';

import { useGetAgentSkills } from 'pages/skills/use-skills';
import type { SkillListItem } from 'pages/skills/skills.types';
import { getAskAgentOwnerToAddMessage } from './ask-agent-owner-message';
import { AgentConfigSkillCard } from './agent-config-skill-card';

type SkillsSectionProps = {
  enabled: boolean;
  canEdit?: boolean;
  selectedSkillIds: string[];
  onRemoveSkill: (skillId: string) => void;
};

export function SkillsSection({
  enabled,
  canEdit = true,
  selectedSkillIds,
  onRemoveSkill,
}: SkillsSectionProps) {
  const { t } = useTranslation();
  const { data: skillsData } = useGetAgentSkills(
    { page: 1, pageSize: 100 },
    { scope: 'agents-v2-config', enabled: selectedSkillIds.length > 0 }
  );

  const skillsById = useMemo(() => {
    const map = new Map<string, SkillListItem>();
    for (const skill of skillsData?.data ?? []) {
      map.set(skill.id, skill);
    }
    return map;
  }, [skillsData?.data]);

  const selectedSkills = useMemo(
    () =>
      selectedSkillIds.map((id) => {
        const skill = skillsById.get(id);
        if (skill) return skill;

        return {
          id,
          name: id,
          displayName: null,
          description: null,
          iconUrl: null,
        } satisfies Pick<SkillListItem, 'id' | 'name' | 'displayName' | 'description' | 'iconUrl'>;
      }),
    [selectedSkillIds, skillsById]
  );

  return (
    <div className="space-y-4">
      {selectedSkills.length > 0 ? (
        <div className="grid gap-4">
          {selectedSkills.map((skill) => (
            <AgentConfigSkillCard
              key={skill.id}
              skill={skill}
              onRemove={onRemoveSkill}
              disabled={!enabled}
            />
          ))}
        </div>
      ) : (
        <div
          className={`flex h-[141px] flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-[#e7e7e7] bg-[#f6f6f6] text-center dark:border-border dark:bg-muted/30 ${!enabled ? 'opacity-50' : ''}`}
        >
          <Sparkles className="size-8 text-[#6d6d6d] dark:text-muted-foreground" strokeWidth={1.5} />
          <p className="max-w-[400px] text-xs font-medium leading-[1.6] text-[#6d6d6d] dark:text-muted-foreground">
            {canEdit
              ? t('NO_SKILLS_ADDED_CLICK_ADD_TO_GET_STARTED') ||
                "No skills added yet. Click 'Add' to get started."
              : getAskAgentOwnerToAddMessage(t, t('SKILLS') || 'skills')}
          </p>
        </div>
      )}
    </div>
  );
}
